/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ThunkDispatch } from "@reduxjs/toolkit";
import { createAction } from "@reduxjs/toolkit";

import jitsuClient from "@/client/jitsu";
import { time } from "@/utils/server-time";

import { JitsuEvent } from "./events/JitsuEvent";

export const onFocus = /* @__PURE__ */ createAction("self/focused");
export const onFocusLost = /* @__PURE__ */ createAction("self/unfocused");
export const onOnline = /* @__PURE__ */ createAction("self/online");
export const onOffline = /* @__PURE__ */ createAction("self/offline");

let initialized = false;

/**
 * A global utility listening to "focus" and "navigator.online" events, and
 * dispatch Redux actions.
 *
 * Taken from `@reduxjs/rtk-query`, where it is used to refetch data and bust
 * the cache.
 *
 * It requires the dispatch method from your store.
 * Calling `setupDOMListeners(store.dispatch)` will configure listeners with the recommended defaults,
 * but you have the option of providing a callback for more granular control.
 *
 * @example
 * ```ts
 * setupListeners(store.dispatch)
 * ```
 *
 * @param dispatch - The dispatch method from your store
 * @param customHandler - An optional callback for more granular control over listener behavior
 * @returns Return value of the handler.
 * The default handler returns an `unsubscribe` method that can be called to remove the listeners.
 */
export function setupDOMListeners(
  dispatch: ThunkDispatch<any, any, any>,
  customHandler?: (
    dispatch: ThunkDispatch<any, any, any>,
    actions: {
      onFocus: typeof onFocus;
      onFocusLost: typeof onFocusLost;
      onOnline: typeof onOnline;
      onOffline: typeof onOffline;
    }
  ) => () => void
) {
  function defaultHandler() {
    let lastOfflineAt: string | null = null;

    const handleOffline = () => {
      lastOfflineAt = new Date(time()).toISOString();
      dispatch(onOffline());
    };

    const handleOnline = () => {
      jitsuClient.track(JitsuEvent.BROWSER_CONNECTION_RESTORED, {
        last_offline_at: lastOfflineAt,
        online_at: new Date(time()).toISOString(),
      });
      dispatch(onOnline());
    };

    const handleFocus = () => dispatch(onFocus());
    const handleFocusLost = () => dispatch(onFocusLost());

    const handleVisibilityChange = () => {
      if (window.document.visibilityState === "visible") {
        handleFocus();
      } else {
        handleFocusLost();
      }
    };

    if (!initialized) {
      if (typeof window !== "undefined" && window.addEventListener) {
        // Handle focus events
        window.addEventListener(
          "visibilitychange",
          handleVisibilityChange,
          false
        );
        window.addEventListener("focus", handleFocus, false);

        // Handle connection events
        window.addEventListener("online", handleOnline, false);
        window.addEventListener("offline", handleOffline, false);
        initialized = true;
      }
    }
    const unsubscribe = () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      initialized = false;
    };
    return unsubscribe;
  }

  return customHandler
    ? customHandler(dispatch, { onFocus, onFocusLost, onOffline, onOnline })
    : defaultHandler();
}
